import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IndicationRequest, UserRecord, userService } from "@/services";
import {
  RegisterStepsModal,
  RegisteStepsrModalData,
  RegistrationType,
  TermsModal,
} from "@/components";

const CompleteRegisterScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const socialData = location.state as {
    uid: string;
    email: string;
    provider: string;
  } | null;

  const [formData, setFormData] = useState<UserRecord>({
    gender: "",
    seeking: "",
  });

  const [touched, setTouched] = useState({
    gender: false,
    seeking: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showTerms, setShowTerms] = useState(false);
  const [showRegisterSteps, setShowRegisterSteps] = useState(false);

  useEffect(() => {
    if (!socialData || !socialData.uid) {
      navigate("/login");
    }
  }, [socialData, navigate]);

  const errors = useMemo(() => {
    return {
      gender: formData.gender ? null : "Selecione uma opção.",
      seeking: formData.seeking ? null : "Selecione uma opção.",
    };
  }, [formData.gender, formData.seeking]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name } = e.target;
    if (name === "gender" || name === "seeking") {
      setTouched((prev) => ({ ...prev, [name]: true }));
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTouched({ gender: true, seeking: true });

    if (errors.gender || errors.seeking) {
      return;
    }

    setShowTerms(true);
  };

  const _createUser = async (modalData: RegisteStepsrModalData) => {
    if (!socialData?.uid) return;

    try {
      setIsSubmitting(true);
      const { profilePhoto, ...restModalData } = modalData;

      const payload: UserRecord = {
        ...formData,
        ...restModalData,
      };

      let userIndication: IndicationRequest | undefined = undefined;
      const params = new URLSearchParams(window.location.search);
      const referrerSlug = params.get("user");
      const referralCode = params.get("ref");

      if (referrerSlug || referralCode) {
        userIndication = {
          referrerSlug: referrerSlug || "",
          referralCode: referralCode || "",
          referredUsername: restModalData.username || "",
        } as IndicationRequest;
      }

      const result = await userService.completeCreateUser(
        socialData.uid,
        socialData.email,
        null,
        payload,
        { profilePhoto, userIndication },
      );

      if (result.success) {
        setShowRegisterSteps(false);
        navigate("/inicio");
      } else {
        const message = result.error.detail ?? result.error.title;
        setShowRegisterSteps(false);
        navigate("/cadastro/erro", { state: { message } });
      }
    } catch (error) {
      console.error(error);
      navigate("/cadastro/erro", {
        state: { message: "Ocorreu um erro inesperado." },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const _completeRegister = (acceptedTerms: boolean) => {
    setShowTerms(false);

    if (acceptedTerms) {
      setFormData((prev) => ({ ...prev, acceptedTerms: true }));
      setShowRegisterSteps(true);
    }
  };

  if (!socialData) return null;

  return (
    <section className="stats">
      <div className="container">
        <div className="stat-card cta-content center auth-card">
          <h1 className="auth-title">Falta muito pouco!</h1>
          <p className="auth-subtitle">
            Escolha suas preferências básicas para o seu perfil.
          </p>
          <form className="auth-form" onSubmit={handleSubmit}>
            <div>
              <label>Qual o seu gênero?</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value="" disabled>
                  Selecione uma opção
                </option>
                <option value="Masculino">Masculino</option>
                <option value="Feminino">Feminino</option>
              </select>
              {touched.gender && errors.gender && (
                <div className="form-error">{errors.gender}</div>
              )}
            </div>

            <div>
              <label>Está em busca de quê?</label>
              <select
                name="seeking"
                value={formData.seeking}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value="" disabled>
                  Selecione uma opção
                </option>
                <option value="Homens">Homens</option>
                <option value="Mulheres">Mulheres</option>
                <option value="Homens e mulheres">Homens e mulheres</option>
              </select>
              {touched.seeking && errors.seeking && (
                <div className="form-error">{errors.seeking}</div>
              )}
            </div>

            <div className="auth-actions">
              <button
                className="btn-primary"
                type="submit"
                disabled={
                  isSubmitting ||
                  Boolean(errors.gender) ||
                  Boolean(errors.seeking)
                }
              >
                {isSubmitting ? "Enviando..." : "Continuar"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <TermsModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        onSubmit={_completeRegister}
      />

      <RegisterStepsModal
        isOpen={showRegisterSteps}
        onClose={() => setShowRegisterSteps(false)}
        onSubmit={_createUser}
        isLoading={isSubmitting}
        registrationType={socialData?.provider as RegistrationType}
      />
    </section>
  );
};

export default CompleteRegisterScreen;
